import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { httpActions } from '@figure/frontlib-onboarding-redux';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  usePersonalLoan,
  usePersonalLoanRouteFsm,
  usePlaid,
  useStorage,
  useOffer,
  useProfile,
  useAch,
  useTelemetry,
} from 'shared/hooks';
import { Vendors } from 'shared/constants';
import { Income } from './';

const IncomeContainer = ({ hasSeenIncomeOption }) => {
  const dispatch = useDispatch();
  const { uuid: personalLoanUuid } = useParams();
  const location = useLocation();
  const history = useHistory();
  const { profile } = useProfile();
  const { personalLoan } = usePersonalLoan();
  const { offerPackage, getOfferPackage } = useOffer();
  const {
    incomeRequestRecords = [],
    incomeResultRecordUuids = [],
    plaidItemUuids = [],
  } = offerPackage;
  const {
    items,
    getPlaidAssets,
    updatePlaidItems,
    updatePlaidItem,
    deletePlaidItem,
    saveFailedPlaidLink,
    serverError: achError,
  } = useAch();
  const { telemetry } = useTelemetry();
  const [plaidError, setPlaidError] = useState('');
  const [counter, setCounter] = useState({ complete: 0, total: 0 });
  const [loaded, setLoaded] = useState(false);
  const [isOpen, setOpen] = useState(false);
  const { loaded: plaidLinkLoaded, error: plaidLinkError, create: plaidLinkCreate } = usePlaid();
  const { storedValue: openValue, setValue: storeOpenValue } = useStorage(
    `iv-modal-${personalLoanUuid}`,
    '',
    'sessionStorage',
    false
  );
  const runRouter = usePersonalLoanRouteFsm();
  const complete = offerPackage.subDecision === 'VERIFIED_INCOME_MATCHED_OFFER';

  const initialize = useCallback(async () => {
    if (personalLoan.status?.uwSubDecision === 'VERIFIED_INCOME_MATCHED_OFFER') {
      return runRouter();
    }

    // assume product selection so wait until VERIFIED_INCOME_MATCHED_OFFER
    if (location.state?.selection) {
      // poll until verified, otherwise timeout
      const httpStart = httpActions.overrideCreator(() => ({ message: '' }));
      const MAX_ATTEMPTS = 20;
      const pollOfferPackage = async (attempts = 0) => {
        dispatch(httpActions.complete());

        if (attempts < MAX_ATTEMPTS) {
          const { subDecision } = await getOfferPackage(personalLoanUuid);

          if (subDecision !== 'VERIFIED_INCOME_MATCHED_OFFER') {
            dispatch(httpStart());
            window.setTimeout(() => pollOfferPackage(attempts + 1), 500);
          }
        } else {
          history.push(`/applications/${personalLoanUuid}/review-app/in-review`);
        }
      };

      await pollOfferPackage();
    } else {
      try {
        const [assets, offerPackage] = await Promise.all([
          getPlaidAssets(),
          getOfferPackage(personalLoanUuid),
        ]);
        const { items = [] } = assets;
        const { plaidItemUuids = [] } = offerPackage;
        // This may be an imperfect check but the update call should be deterministic in terms of input/output results
        if (items.some((item) => item.assetsComplete) && plaidItemUuids.length < 1) {
          await updatePlaidItems();
        }
      } catch (e) {
        // Just in case
        await updatePlaidItems();
      }
    }
  }, []);

  useEffect(() => {
    (async () => {
      await initialize();
      setLoaded(true);
    })();
  }, [initialize]);

  useEffect(() => {
    if (!openValue && loaded && !isOpen && counter.total === 0) {
      storeOpenValue('true');
      setOpen(true);
    }
  }, [loaded, isOpen, counter, openValue, storeOpenValue]);

  useEffect(() => {
    let mounted = true;
    let timeoutId;

    const pollHandler = () => {
      timeoutId = window.setTimeout(async () => {
        try {
          await Promise.all([getPlaidAssets(false), getOfferPackage(personalLoanUuid, false)]);
        } finally {
          if (mounted) pollHandler();
        }
      }, 3000);
    };

    pollHandler();

    return () => {
      window.clearTimeout(timeoutId);
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const paystubIncomeRecords = incomeRequestRecords.filter(
      (r) => r.vendor === Vendors.POINTSERV || r.vendor === Vendors.TRUEWORK
    );
    const paystubTotal = paystubIncomeRecords.length;
    const paystubComplete = paystubIncomeRecords.filter((r) =>
      incomeResultRecordUuids.includes(r.uuid)
    ).length;
    const plaidTotal = items.length;
    const plaidComplete = items.filter((item) => plaidItemUuids.includes(item.uuid)).length;

    setCounter({ complete: paystubComplete + plaidComplete, total: paystubTotal + plaidTotal });
  }, [setCounter, incomeRequestRecords, incomeResultRecordUuids, plaidItemUuids, items]);

  useEffect(() => {
    if (complete) telemetry('plIncomeVerified');
  }, [complete, telemetry]);

  const onPlaidRetry = async (item) => {
    try {
      await updatePlaidItem(item.uuid);
      await getPlaidAssets();
    } catch (e) {
      setPlaidError('Unable to retry');
    }
  };

  const openPlaidLink = (item, onSuccess) =>
    plaidLinkCreate({
      itemUuid: item?.uuid,
      onSuccess,
      onExit: (error, meta) => {
        if (error) {
          setPlaidError('Unable to link account');
          onPlaidError(error, meta);
        }
      },
    });

  const handleLink = (type) =>
    history.push(`/applications/${personalLoanUuid}/income/link/${type}`);

  const handlePlaidDelete = async (item) => {
    await deletePlaidItem(item.uuid);
    await getPlaidAssets();
  };

  const onPlaidError = async (linkError, linkErrorMetadata) =>
    saveFailedPlaidLink({ linkError, linkErrorMetadata });

  const handleSubmit = (complete) => {
    if (complete) {
      runRouter();
    } else {
      history.push({
        pathname: `/applications/${personalLoanUuid}/offer-select`,
        state: {
          from: {
            pathname: history.location.pathname,
            state: { selection: true },
          },
        },
      });
    }
  };
  if (!loaded) return null;

  return (
    <Income
      complete={complete}
      firstName={profile.name.firstName}
      counter={counter}
      offerPackage={offerPackage}
      items={items}
      onPlaidDelete={handlePlaidDelete}
      openPlaidLink={openPlaidLink}
      plaidLinkLoaded={plaidLinkLoaded}
      onPlaidRetry={onPlaidRetry}
      onLink={handleLink}
      error={achError || plaidError || plaidLinkError || ''}
      onSubmit={handleSubmit}
      onClose={() => setOpen(false)}
      personalLoan={personalLoan}
      isOpen={isOpen}
      hasSeenIncomeOption={hasSeenIncomeOption}
    />
  );
};

IncomeContainer.propTypes = {
  hasSeenIncomeOption: PropTypes.bool.isRequired,
};

export default IncomeContainer;
